import React, { createContext, useContext } from 'react';

const OPERATOR_TOKENS = new Set(['and', 'or', 'not', 'и', 'или', 'не']);

export const extractSearchHighlightTerms = (...sources: Array<string | null | undefined>): string[] => {
  const terms = new Set<string>();

  sources.forEach((source) => {
    if (!source) return;
    const normalized = source
      .replace(/[()[\]{},]/g, ' ')
      .replace(/"/g, ' ')
      .trim();
    if (!normalized) return;

    normalized.split(/\s+/).forEach((token) => {
      const cleaned = token.replace(/^[^0-9A-Za-zА-Яа-яЁё+_./-]+|[^0-9A-Za-zА-Яа-яЁё+_./-]+$/g, '');
      if (!cleaned || cleaned.length < 2) return;
      if (OPERATOR_TOKENS.has(cleaned.toLowerCase())) return;
      terms.add(cleaned);
    });
  });

  return Array.from(terms).sort((left, right) => right.length - left.length);
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const highlightSearchText = (
  text: string,
  terms: string[],
): React.ReactNode => {
  if (!text || terms.length === 0) return text;

  const pattern = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  const parts = text.split(pattern);
  if (parts.length === 1) return text;

  return parts.map((part, index) => {
    if (!part) return null;
    const isMatch = terms.some((term) => term.toLowerCase() === part.toLowerCase());
    if (!isMatch) return <React.Fragment key={`t-${index}`}>{part}</React.Fragment>;
    return (
      <mark
        key={`h-${index}`}
        className="rounded-[3px] bg-[#FFE566] px-0.5 text-inherit"
      >
        {part}
      </mark>
    );
  });
};

const SearchHighlightContext = createContext<string[]>([]);

export const SearchHighlightProvider = ({
  terms,
  children,
}: {
  terms: string[];
  children: React.ReactNode;
}) => (
  <SearchHighlightContext.Provider value={terms}>
    {children}
  </SearchHighlightContext.Provider>
);

export const useSearchHighlightTerms = (): string[] => useContext(SearchHighlightContext);

export const HighlightedText = ({
  text,
  className,
}: {
  text: string;
  className?: string;
}) => {
  const terms = useSearchHighlightTerms();
  const content = highlightSearchText(text, terms);
  if (!className) return <>{content}</>;
  return <span className={className}>{content}</span>;
};
